import { frozenEvaluationBindingSchema } from "./frozen-evaluation.js";
import { z } from "zod";
import { runIdSchema, activeTaskRunRuntimeBackendSchema, taskIdSchema, taskRunCapabilitySchema, taskRunEventPayloadSchema, taskRunExecutionTierSchema, taskRunRoutingFailureSchema, taskRunInteractionModeSchema, taskRunRuntimeBackendSchema, taskRunSourceSchema, taskRunStatusSchema, runErrorCodeSchema, governedPromptProfileSchema, openAICodexExperimentSchema, taskAgentStepReasoningEnabledSchema, taskAgentCacheOptimizationEnabledSchema, sessionRoutingArchitectureSchema, } from "./primitives.js";
import { runPerformanceMetricsSchema } from "./metrics.js";
import { runtimePluginSkillOverlaySchema } from "./runtime-plugins.js";
import { runModelConfigSchema } from "./model-config.js";
import { modelGatewayErrorOriginSchema, modelGatewayFailureKindSchema, providerFinishReasonSchema } from "./model-gateway.js";
import { agentPromptSurfaceSchema } from "./agent-prompts.js";
import { twinExecutionCertificateRefSchema, twinExecutionCertificateRefsSchema, twinInvocationExecutionReceiptV1Schema, } from "./twin-execution-certificates.js";
import { candidateComponentBuildAttestationSchema } from "./candidate-build.js";
import { memoryTraceEventSchema } from "./memory-trace.js";
import { DISABLED_MEMORY_ROLLOUT_MODE, memoryRolloutModeSchema, } from "./memory-policy.js";
import { readNumber, readObject, readString } from "./value-readers.js";
export const createTaskRunInputSchema = z.object({
    taskId: taskIdSchema,
    sessionId: z.string().trim().min(1).optional(),
    organisationId: z.string().trim().min(1).optional(),
    userId: z.string().trim().min(1),
    prompt: z.string().trim().min(1),
    batchId: z.string().trim().min(1).optional(),
    scenario: z.string().trim().min(1).optional(),
    agentProfileSlug: z.string().trim().min(1).max(120).optional(),
    requestedSkillSlugs: z
        .array(z.string().trim().min(1).max(120))
        .max(32)
        .optional(),
    requestedPluginSlugs: z
        .array(z.string().trim().min(1).max(120))
        .max(32)
        .optional(),
    source: taskRunSourceSchema.default("web"),
    requestedCapabilities: z
        .array(taskRunCapabilitySchema)
        .min(1)
        .default(["readonly-data", "writeback"]),
    interactionMode: taskRunInteractionModeSchema.default("foreground"),
    runtimeBackend: activeTaskRunRuntimeBackendSchema.default("electric"),
    modelConfig: runModelConfigSchema.optional(),
    taskAgentStepReasoningEnabled: taskAgentStepReasoningEnabledSchema.optional(),
    taskAgentCacheOptimizationEnabled: taskAgentCacheOptimizationEnabledSchema.optional(),
    // AGE-18: opt-in multi-turn. When set, the compiler hydrates the prior
    // run's transcript into the new run's system prompt under a
    // `## Prior conversation` section. Cross-tenant or unknown id → 404
    // problem+json (HTTP-handler precheck via `getRun`).
    priorRunId: runIdSchema.optional(),
});
export const listTaskRunsInputSchema = z.object({
    taskId: taskIdSchema.optional(),
});
export const getTaskRunInputSchema = z.object({
    runId: runIdSchema,
});
export const taskRunsQueryInputSchema = z.object({
    runId: runIdSchema.optional(),
    taskId: taskIdSchema.optional(),
    fields: z.enum(["full", "summary", "answer", "history"]).optional(),
    eventCursor: z.string().min(1).max(1000).optional(),
}).refine(value => value.fields !== "answer" || Boolean(value.runId), {
    message: "Answer presentation requires a runId", path: ["runId"],
}).refine(value => (!value.eventCursor || value.fields === "history") &&
    (value.fields !== "history" || Boolean(value.runId)), {
    message: "History cursors require a runId and fields=history", path: ["eventCursor"],
});
export const governedNonAnswerableSchema = z.object({
    kind: z.enum(["needs_clarification", "unsupported"]),
    unansweredRequest: z.string().trim().min(1).max(500),
    explanation: z.string().trim().min(1).max(500),
    missingFields: z.array(z.string().trim().min(1).max(120)).max(8),
    suggestedFollowUp: z.string().trim().min(1).max(600),
    customSqlFallback: z.object({
        label: z.literal("Use custom SQL instead"),
        warning: z.string().trim().min(1).max(600),
        confirmation: z.string().trim().min(1).max(600),
    }).strict(),
}).strict();
export const GOVERNED_CUSTOM_SQL_WARNING_VERSION = "mlx.governed-custom-sql-warning.v1";
export const governedCustomSqlWarningVersionSchema = z.literal(GOVERNED_CUSTOM_SQL_WARNING_VERSION);
export const createGovernedCustomSqlEscalationInputSchema = z.object({
    idempotencyKey: z.string().trim().min(8).max(160)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/u, "Expected an idempotency key"),
    confirmation: z.object({
        accepted: z.literal(true),
        warningVersion: governedCustomSqlWarningVersionSchema,
    }).strict(),
}).strict();
export const governedCustomSqlEscalationResponseSchema = z.object({
    escalationId: z.string().trim().min(1),
    parentRunId: runIdSchema,
    runId: runIdSchema,
    sessionId: z.string().trim().min(1),
    status: taskRunStatusSchema,
    created: z.boolean(),
    resultGovernance: z.literal("ungoverned"),
    warningVersion: governedCustomSqlWarningVersionSchema,
    warning: z.string().trim().min(1).max(600),
    confirmationRecordedAt: z.number(),
}).strict();
/** A file attached to one turn, as far as the viewer may open it. */
export const taskRunAttachedFileSchema = z.object({
    id: z.string().min(1),
    fileName: z.string(),
    contentType: z.string(),
    byteSize: z.number().nonnegative(),
});
/** The channel, Data set, view or Twin entity a turn was asked about, under its current name. */
export const taskRunMentionedResourceSchema = z.object({
    type: z.enum(["channel", "dataSet", "view", "entity"]),
    id: z.string().min(1),
    name: z.string(),
});
export const taskRunSummarySchema = z.object({
    id: runIdSchema,
    taskId: taskIdSchema,
    userId: z.string(),
    verifiedSubjectId: z.string().optional(),
    pluginSubjectId: z.string().optional(),
    channelAccessBypass: z.literal(false).default(false),
    queueShard: z.number().optional(),
    source: taskRunSourceSchema,
    prompt: z.string(),
    userMessageContent: z.string(),
    // Files attached to this turn; run lists include them, other reads omit them.
    attachedFiles: z.array(taskRunAttachedFileSchema).max(50).optional(),
    // A channel, Data set and view, then up to 10 Twin entities.
    mentionedResources: z.array(taskRunMentionedResourceSchema).max(13).optional(),
    batchId: z.string().optional(),
    scenario: z.string().optional(),
    runRole: z.enum(["task_agent", "retrieval_agent"]).default("task_agent"),
    routingArchitecture: sessionRoutingArchitectureSchema.default("legacy_router"),
    governedPromptProfile: governedPromptProfileSchema.default("fat"),
    openAICodexExperiment: openAICodexExperimentSchema.optional(),
    // Populated from the immutable run.queued event for executable worker
    // claims. Ordinary run summaries omit these request-scoped controls.
    taskAgentStepReasoningEnabled: taskAgentStepReasoningEnabledSchema.optional(),
    taskAgentCacheOptimizationEnabled: taskAgentCacheOptimizationEnabledSchema.optional(),
    parentRunId: runIdSchema.optional(),
    memoryRolloutMode: memoryRolloutModeSchema.default(DISABLED_MEMORY_ROLLOUT_MODE),
    tenantSlug: z.string().optional(),
    requestedPluginSlugs: z.array(z.string()).optional(),
    resolvedPluginSlugs: z.array(z.string()).optional(),
    agentProfileId: z.string().optional(),
    taskRouterVersionId: z.string().optional(),
    requestedSkillSlugs: z.array(z.string()),
    matchedTaskRouterRuleKeys: z.array(z.string()),
    selectedSkillSlugs: z.array(z.string()).optional(),
    dependencySkillSlugs: z.array(z.string()).optional(),
    resolvedSkillSlugs: z.array(z.string()),
    executedSkillSlugs: z.array(z.string()).optional(),
    resolvedSkillVersionIds: z.array(z.string()),
    resolvedPluginSkillOverlays: z
        .array(runtimePluginSkillOverlaySchema)
        .default([]),
    ambientContextVersionIds: z.array(z.string()),
    agentPromptVersionIds: z.array(z.string()).default([]),
    agentPromptSnapshot: z.array(agentPromptSurfaceSchema).default([]),
    systemPromptHash: z.string().optional(),
    compiledPromptStorageId: z.string().optional(),
    // AGE-18: round-tripped on reads so the SDK consumer can chain follow-ups
    // (`priorRunId === runId` of the previous turn) without keeping its own
    // mapping. Optional because pre-AGE-18 runs and runs without a prior
    // never set this field.
    priorRunId: runIdSchema.optional(),
    requestedCapabilities: z.array(taskRunCapabilitySchema),
    interactionMode: taskRunInteractionModeSchema,
    runtimeBackend: taskRunRuntimeBackendSchema,
    // NULL identifies runs created before execution-tier persistence shipped.
    // Every new run writer persists an explicit tier.
    executionTier: taskRunExecutionTierSchema.nullable(),
    executionLane: z.string().regex(/^(?:production|candidate:[0-9a-f]{7,64}|evaluation:[0-9a-f]{64})$/u).default("production"),
    sessionId: z.string().trim().min(1).optional(),
    status: taskRunStatusSchema,
    responseText: z.string().optional(),
    errorMessage: z.string().optional(),
    errorCode: runErrorCodeSchema.optional(),
    /**
     * Where a failed run's model-call failure came from (the model gateway's envelope): `upstream`
     * only when the provider failed it, `gateway` when the gateway did. Absent when the failure
     * never came through the gateway, which makes it MLX's own.
     */
    errorOrigin: modelGatewayErrorOriginSchema.optional(),
    /** What the failure was: an upstream kind (provider_unreachable, …) or the gateway's own code. */
    errorKind: modelGatewayFailureKindSchema.optional(),
    /** Why the provider ended its reply, for `provider_stopped` (Gemini's finishReason: SAFETY, …). */
    errorFinishReason: providerFinishReasonSchema.optional(),
    governedNonAnswerable: governedNonAnswerableSchema.optional(),
    resultGovernance: z.literal("ungoverned").optional(),
    resultGovernanceLabel: z.literal("Ungoverned custom SQL result — outside governed-contract guarantees").optional(),
    leaseOwner: z.string().optional(),
    leaseExpiresAt: z.number().optional(),
    heartbeatAt: z.number().optional(),
    startedAt: z.number().optional(),
    runtimeStartedAt: z.number().optional(),
    firstAssistantDeltaAt: z.number().optional(),
    firstTextDeltaAt: z.number().optional(),
    finishedAt: z.number().optional(),
    eventCount: z.number().optional(),
    workerId: z.string().optional(),
    runtimeMode: z.string().optional(),
    provider: z.string().optional(),
    model: z.string().optional(),
    modelConfig: runModelConfigSchema.optional(),
    frozenEvaluation: frozenEvaluationBindingSchema.optional(),
    metrics: runPerformanceMetricsSchema.optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
});
/**
 * Strict worker/runtime handoff. Historical API reads retain the legacy
 * default on `taskRunSummarySchema`, but executable claims must carry the
 * architecture that the control plane actually persisted.
 */
export const executableTaskRunSummarySchema = taskRunSummarySchema.and(z.object({ routingArchitecture: sessionRoutingArchitectureSchema }));
function uniqueSkillSlugs(values) {
    if (!Array.isArray(values))
        return [];
    return [...new Set(values
            .filter((value) => typeof value === "string")
            .map((value) => value.trim())
            .filter(Boolean))];
}
function parsedToolResult(value) {
    if (typeof value !== "string")
        return readObject(value);
    try {
        return readObject(JSON.parse(value));
    }
    catch {
        return undefined;
    }
}
export function loadedSkillContextTelemetryFromPayload(payload) {
    const eventPayload = readObject(payload);
    if (readString(eventPayload?.toolName) !== "load_skill_context")
        return null;
    const result = parsedToolResult(eventPayload?.result);
    const nestedResult = parsedToolResult(result?.result);
    const details = readObject(result?.details) ?? readObject(nestedResult?.details);
    const args = readObject(eventPayload?.args);
    const baseSkillSlugs = uniqueSkillSlugs(details?.skillSlugs);
    const overlaySkillSlugs = uniqueSkillSlugs(details?.overlaySkillSlugs);
    const exact = baseSkillSlugs.length > 0 || overlaySkillSlugs.length > 0;
    const fallbackSkillSlugs = exact ? [] : uniqueSkillSlugs(args?.skillSlugs);
    const effectiveBaseSkillSlugs = exact ? baseSkillSlugs : fallbackSkillSlugs;
    const requestedSkillSlugs = uniqueSkillSlugs(details?.requestedSkillSlugs ?? args?.skillSlugs);
    const dependencySkillSlugs = uniqueSkillSlugs(details?.dependencySkillSlugs);
    const contextCharacters = readNumber(details?.contextCharacters) ?? 0;
    return {
        requestedSkillSlugs,
        baseSkillSlugs: effectiveBaseSkillSlugs,
        dependencySkillSlugs,
        overlaySkillSlugs,
        allSkillSlugs: uniqueSkillSlugs([
            ...effectiveBaseSkillSlugs,
            ...overlaySkillSlugs,
        ]),
        contextCharacters,
        loadCount: 1,
        exact,
    };
}
export function summarizeLoadedSkillContextEvents(events) {
    const loads = events
        .filter((event) => !event.kind || event.kind === "tool.completed")
        .map((event) => loadedSkillContextTelemetryFromPayload(event.payload))
        .filter((load) => Boolean(load));
    return {
        requestedSkillSlugs: uniqueSkillSlugs(loads.flatMap((load) => load.requestedSkillSlugs)),
        baseSkillSlugs: uniqueSkillSlugs(loads.flatMap((load) => load.baseSkillSlugs)),
        dependencySkillSlugs: uniqueSkillSlugs(loads.flatMap((load) => load.dependencySkillSlugs)),
        overlaySkillSlugs: uniqueSkillSlugs(loads.flatMap((load) => load.overlaySkillSlugs)),
        allSkillSlugs: uniqueSkillSlugs(loads.flatMap((load) => load.allSkillSlugs)),
        contextCharacters: loads.reduce((sum, load) => sum + load.contextCharacters, 0),
        loadCount: loads.length,
        exact: loads.length > 0 && loads.every((load) => load.exact),
    };
}
export const assistantPartTypeSchema = z.enum([
    "text",
    "reasoning",
    "toolCall",
    "artifactRef",
    "citationSource",
    "report",
    "editorDoc",
]);
const taskRunEventMetaShape = {
    id: z.string().trim().min(1),
    runId: runIdSchema,
    createdAt: z.number(),
    // Durable per-run order from the control plane; created_at is a synthetic
    // batch-local timestamp and must not be used to order events when seq is
    // present.
    seq: z.number().optional(),
};
const jsonRecordSchema = z.record(z.string(), z.unknown());
const runQueuedPayloadSchema = z.object({
    frozenEvaluation: frozenEvaluationBindingSchema.optional(),
    source: taskRunSourceSchema.optional(),
    interactionMode: taskRunInteractionModeSchema.optional(),
    batchId: z.string().optional(),
    scenario: z.string().optional(),
    runtimeBackend: taskRunRuntimeBackendSchema.optional(),
    // Retain the routed configuration on reads so run detail views can show
    // the model's configured reasoning mode for historical and live runs.
    modelConfig: runModelConfigSchema.optional(),
    governedPromptProfile: governedPromptProfileSchema.optional(),
    openAICodexExperiment: openAICodexExperimentSchema.optional(),
    taskAgentStepReasoningEnabled: taskAgentStepReasoningEnabledSchema.optional(),
    taskAgentCacheOptimizationEnabled: taskAgentCacheOptimizationEnabledSchema.optional(),
    requestedPluginSlugs: z.array(z.string()).default([]),
    resolvedPluginSlugs: z.array(z.string()).default([]),
    executionTier: taskRunExecutionTierSchema.optional(),
    routingFailure: taskRunRoutingFailureSchema.optional(),
});
const runResolvedPayloadSchema = z.object({
    agentProfileId: z.string().optional(),
    taskRouterVersionId: z.string().optional(),
    requestedPluginSlugs: z.array(z.string()).default([]),
    resolvedPluginSlugs: z.array(z.string()).default([]),
    matchedTaskRouterRuleKeys: z.array(z.string()).default([]),
    selectedSkillSlugs: z.array(z.string()).optional(),
    dependencySkillSlugs: z.array(z.string()).optional(),
    resolvedSkillSlugs: z.array(z.string()).default([]),
    resolvedSkillVersionIds: z.array(z.string()).default([]),
    ambientContextVersionIds: z.array(z.string()).default([]),
    executionTier: taskRunExecutionTierSchema.optional(),
    routingFailure: taskRunRoutingFailureSchema.optional(),
});
const runCompiledPayloadSchema = z.object({
    systemPromptHash: z.string().optional(),
    compiledPromptStorageId: z.string().optional(),
    bytes: z.number().optional(),
    // AGE-18: prior-context metadata stamped only when the run had a
    // `priorRunId`. All four fields are optional so existing payloads (no
    // prior context) parse unchanged — additive widening only.
    priorRunId: z.string().optional(),
    priorRunStatusAtCompile: taskRunStatusSchema.optional(),
    priorTurnCount: z.number().int().nonnegative().optional(),
    priorTruncatedTurns: z.number().int().nonnegative().optional(),
    priorArtifactCount: z.number().int().nonnegative().optional(),
    priorTruncatedArtifacts: z.number().int().nonnegative().optional(),
});
const workerLeasePayloadSchema = z.object({
    workerId: z.string().trim().min(1),
});
const workerStartedPayloadSchema = z.object({
    runtimeMode: z.string().trim().min(1),
});
const runtimeStartedPayloadSchema = z.object({
    mode: z.string().trim().min(1),
    provider: z.string().trim().min(1).optional(),
    model: z.string().trim().min(1).optional(),
    taskTitle: z.string().trim().min(1).optional(),
});
const runtimeProgressPayloadSchema = z.object({
    message: z.string().trim().min(1),
});
export const providerProgressPayloadSchema = z.object({
    provider: z.literal("runpod"),
    backend: z.literal("serverless"),
    stage: z.enum([
        "request_preparing",
        "job_submitting",
        "job_queued",
        "job_running",
        "job_completed",
        "job_failed",
        "response_validating",
        "response_ready",
    ]),
    auditId: z.string().trim().min(1),
    jobId: z.string().trim().min(1).optional(),
    providerState: z.string().trim().min(1).optional(),
    diagnosticCode: z.string().trim().min(1).optional(),
    detail: z.string().trim().min(1).max(500).optional(),
    elapsedMs: z.number().int().nonnegative().optional(),
    stageOneMs: z.number().int().nonnegative().optional(),
    stageTwoMs: z.number().int().nonnegative().optional(),
});
const assistantPartDeltaPayloadSchema = z.object({
    messageId: z.string().trim().min(1).optional(),
    partId: z.string().trim().min(1),
    partType: assistantPartTypeSchema,
    contentIndex: z.number().int().nonnegative(),
    delta: z.string(),
    mimeType: z.string().trim().min(1).optional(),
    metadata: jsonRecordSchema.optional(),
});
const toolCallContentSchema = z.object({
    id: z.string().trim().min(1),
    name: z.string().trim().min(1),
    arguments: jsonRecordSchema,
});
const toolPresentationSchema = z.object({
    action: z.string().trim().min(1).max(240).optional(),
    result: z.string().trim().min(1).max(240).optional(),
}).strict();
const assistantPartCompletedPayloadSchema = z.object({
    messageId: z.string().trim().min(1).optional(),
    partId: z.string().trim().min(1),
    partType: assistantPartTypeSchema,
    contentIndex: z.number().int().nonnegative(),
    text: z.string().optional(),
    toolCall: toolCallContentSchema.optional(),
    mimeType: z.string().trim().min(1).optional(),
    metadata: jsonRecordSchema.optional(),
});
const toolStartedPayloadSchema = z.object({
    toolCallId: z.string().trim().min(1),
    toolName: z.string().trim().min(1),
    toolLabel: z.string().trim().min(1).optional(),
    pluginSlug: z.string().trim().min(1).optional(),
    pluginLabel: z.string().trim().min(1).optional(),
    adapterSlug: z.string().trim().min(1).optional(),
    runtimeSurface: z.string().trim().min(1).optional(),
    scope: jsonRecordSchema.optional(),
    presentation: toolPresentationSchema.optional(),
    args: z.unknown(),
    startedAt: z.number().nonnegative().optional(),
});
const toolUpdatedPayloadSchema = z.object({
    toolCallId: z.string().trim().min(1),
    toolName: z.string().trim().min(1),
    toolLabel: z.string().trim().min(1).optional(),
    pluginSlug: z.string().trim().min(1).optional(),
    pluginLabel: z.string().trim().min(1).optional(),
    adapterSlug: z.string().trim().min(1).optional(),
    runtimeSurface: z.string().trim().min(1).optional(),
    scope: jsonRecordSchema.optional(),
    presentation: toolPresentationSchema.optional(),
    args: z.unknown(),
    partialResult: z.unknown().optional(),
    startedAt: z.number().nonnegative().optional(),
    updatedAt: z.number().nonnegative().optional(),
});
const toolCompletedPayloadSchema = z.object({
    toolCallId: z.string().trim().min(1),
    toolName: z.string().trim().min(1),
    toolLabel: z.string().trim().min(1).optional(),
    pluginSlug: z.string().trim().min(1).optional(),
    pluginLabel: z.string().trim().min(1).optional(),
    adapterSlug: z.string().trim().min(1).optional(),
    runtimeSurface: z.string().trim().min(1).optional(),
    provider: z.string().trim().min(1).optional(),
    operation: z.string().trim().min(1).optional(),
    providerRequestId: z.string().trim().min(1).optional(),
    usage: jsonRecordSchema.optional(),
    scope: jsonRecordSchema.optional(),
    presentation: toolPresentationSchema.optional(),
    resultSize: jsonRecordSchema.optional(),
    args: z.unknown().optional(),
    result: z.unknown(),
    isError: z.boolean().optional(),
    queriedTables: z.array(z.object({
        schema: z.string().trim().min(1),
        table: z.string().trim().min(1),
    }).strict()).optional(),
    queriedRelations: z.array(z.object({
        schemaName: z.string().trim().min(1),
        relationName: z.string().trim().min(1),
        sourceSystem: z.string().trim().min(1).optional(),
    }).strict()).optional(),
    executionReceipt: twinInvocationExecutionReceiptV1Schema.optional(),
    certificateRef: twinExecutionCertificateRefSchema.optional(),
    startedAt: z.number().nonnegative().optional(),
    finishedAt: z.number().nonnegative().optional(),
    durationMs: z.number().nonnegative().optional(),
});
const runCompletedPayloadSchema = z.object({
    outputText: z.string(),
    certificateRefs: twinExecutionCertificateRefsSchema.optional(),
    executionTier: taskRunExecutionTierSchema.optional(),
});
const runFailedPayloadSchema = z.object({
    errorMessage: z.string(),
    errorCode: runErrorCodeSchema.optional(),
    errorOrigin: modelGatewayErrorOriginSchema.optional(),
    errorKind: modelGatewayFailureKindSchema.optional(),
    errorFinishReason: providerFinishReasonSchema.optional(),
});
export const retrievalProgressStageSchema = z.enum([
    "starting",
    "running",
    "context",
    "refining",
    "querying",
    "reviewing",
    "retrying",
    "completed",
    "failed",
    "cancelled",
]);
export const retrievalProgressPayloadSchema = z.object({
    stage: retrievalProgressStageSchema,
    childRunId: runIdSchema,
    childSessionId: z.string().trim().min(1).optional(),
    batchId: z.string().trim().min(1).optional(),
    activityLabel: z.string().trim().min(1).max(140).optional(),
    role: z.literal("governed_retrieval").default("governed_retrieval"),
    childEventId: z.string().trim().min(1).optional(),
    startedAt: z.number().nonnegative().optional(),
    finishedAt: z.number().nonnegative().optional(),
    errorMessage: z.string().trim().min(1).optional(),
    retryCount: z.number().int().nonnegative().optional(),
    retryAfter: z.number().nonnegative().optional(),
});
export const outputCheckpointReachedPayloadSchema = z.object({
    checkpointTokens: z.number().int().positive(),
    stepOutputTokens: z.number().int().positive(),
    contextInputTokens: z.number().int().nonnegative().nullable(),
    contextWindowTokens: z.number().int().positive(),
    remainingContextTokens: z.number().int().nonnegative().nullable(),
    canContinue: z.boolean(),
}).strict();
export const memoryRetrievalProgressStageSchema = z.enum([
    "starting",
    "searching",
    "embedding",
    "ranking",
    "completed",
    "failed",
]);
export const memoryRetrievalMatchSchema = z.object({
    sessionId: z.string().trim().min(1),
    title: z.string().trim().min(1).max(500),
    snippet: z.string().max(500),
    matchKind: z.enum(["title", "user", "assistant"]),
    matchedAt: z.number().finite(),
    // Optional while control-plane and app versions converge. This is the
    // originating task/session creation time, not the matched segment time.
    sourceTaskCreatedAt: z.number().finite().optional(),
    sessionUrl: z.string().url(),
});
export const memoryRetrievalProgressPayloadSchema = z.object({
    stage: memoryRetrievalProgressStageSchema,
    retrievalId: z.string().trim().min(1),
    requestedMode: z.enum(["auto", "exact", "semantic", "hybrid"]),
    effectiveMode: z.enum(["exact", "semantic", "hybrid"]).optional(),
    role: z.literal("episodic_memory").default("episodic_memory"),
    resultPurpose: z.enum(["search_results", "model_context"]).default("search_results"),
    matchCount: z.number().int().nonnegative().optional(),
    matches: z.array(memoryRetrievalMatchSchema).max(20).optional(),
    startedAt: z.number().nonnegative(),
    finishedAt: z.number().nonnegative().optional(),
    durationMs: z.number().nonnegative().optional(),
    errorMessage: z.string().trim().min(1).optional(),
});
function taskRunEventOf(kind, payload) {
    return z.object({
        ...taskRunEventMetaShape,
        kind: z.literal(kind),
        payload,
    });
}
const knownTaskRunEventSchema = z.union([
    taskRunEventOf("run.queued", runQueuedPayloadSchema),
    taskRunEventOf("run.resolved", runResolvedPayloadSchema),
    taskRunEventOf("run.compiled", runCompiledPayloadSchema),
    taskRunEventOf("run.claimed", workerLeasePayloadSchema),
    taskRunEventOf("run.reclaimed", workerLeasePayloadSchema),
    taskRunEventOf("worker.started", workerStartedPayloadSchema),
    taskRunEventOf("runtime.started", runtimeStartedPayloadSchema),
    taskRunEventOf("runtime.progress", runtimeProgressPayloadSchema),
    taskRunEventOf("provider.progress", providerProgressPayloadSchema),
    taskRunEventOf("output.checkpoint.reached", outputCheckpointReachedPayloadSchema),
    taskRunEventOf("retrieval.progress", retrievalProgressPayloadSchema),
    taskRunEventOf("memory.retrieval.progress", memoryRetrievalProgressPayloadSchema),
    taskRunEventOf("memory.trace", memoryTraceEventSchema),
    taskRunEventOf("assistant.part.delta", assistantPartDeltaPayloadSchema),
    taskRunEventOf("assistant.part.completed", assistantPartCompletedPayloadSchema),
    taskRunEventOf("tool.started", toolStartedPayloadSchema),
    taskRunEventOf("tool.updated", toolUpdatedPayloadSchema),
    taskRunEventOf("tool.completed", toolCompletedPayloadSchema),
    taskRunEventOf("tool.failed", toolCompletedPayloadSchema),
    taskRunEventOf("run.completed", runCompletedPayloadSchema),
    taskRunEventOf("run.failed", runFailedPayloadSchema),
]);
const genericTaskRunEventSchema = z.object({
    ...taskRunEventMetaShape,
    kind: z.string().trim().min(1),
    payload: taskRunEventPayloadSchema,
});
export const taskRunEventSchema = z.union([
    knownTaskRunEventSchema,
    genericTaskRunEventSchema,
]);
function normalizeAssistantPartPayload(input) {
    return {
        messageId: readString(input.payload.messageId) ?? undefined,
        partId: readString(input.payload.partId) ?? input.fallbackPartId,
        partType: assistantPartTypeSchema.safeParse(input.payload.partType).success
            ? input.payload.partType
            : input.fallbackPartType,
        contentIndex: Math.max(0, Math.floor(readNumber(input.payload.contentIndex) ?? 0)),
    };
}
export function normalizeTaskRunEvent(input) {
    const base = {
        id: input.id,
        runId: input.runId,
        createdAt: input.createdAt,
        seq: input.seq,
    };
    switch (input.kind) {
        case "assistant.delta": {
            const normalized = normalizeAssistantPartPayload({
                payload: input.payload,
                fallbackPartType: "text",
                fallbackPartId: "assistant:text:0",
            });
            return taskRunEventSchema.parse({
                ...base,
                kind: "assistant.part.delta",
                payload: {
                    ...normalized,
                    delta: readString(input.payload.delta) ?? "",
                },
            });
        }
        case "assistant.part.delta": {
            const normalized = normalizeAssistantPartPayload({
                payload: input.payload,
                fallbackPartType: "text",
                fallbackPartId: "assistant:text:0",
            });
            return taskRunEventSchema.parse({
                ...base,
                kind: "assistant.part.delta",
                payload: {
                    ...normalized,
                    delta: readString(input.payload.delta) ?? "",
                    mimeType: readString(input.payload.mimeType) ?? undefined,
                    metadata: readObject(input.payload.metadata) ?? undefined,
                },
            });
        }
        case "assistant.part.completed": {
            const normalized = normalizeAssistantPartPayload({
                payload: input.payload,
                fallbackPartType: "text",
                fallbackPartId: "assistant:text:0",
            });
            const toolCall = readObject(input.payload.toolCall);
            const toolCallArgs = readObject(toolCall?.arguments);
            return taskRunEventSchema.parse({
                ...base,
                kind: "assistant.part.completed",
                payload: {
                    ...normalized,
                    text: readString(input.payload.text) ?? undefined,
                    mimeType: readString(input.payload.mimeType) ?? undefined,
                    metadata: readObject(input.payload.metadata) ?? undefined,
                    toolCall: toolCall &&
                        readString(toolCall.id) &&
                        readString(toolCall.name) &&
                        toolCallArgs
                        ? {
                            id: readString(toolCall.id),
                            name: readString(toolCall.name),
                            arguments: toolCallArgs,
                        }
                        : undefined,
                },
            });
        }
        case "tool.started":
        case "tool.updated":
        case "tool.completed":
        case "tool.failed": {
            const toolName = readString(input.payload.toolName) ?? "unknown_tool";
            const toolCallId = readString(input.payload.toolCallId) ??
                `${toolName}:${input.id}`;
            const shared = {
                toolCallId,
                toolName,
                toolLabel: readString(input.payload.toolLabel) ?? undefined,
                pluginSlug: readString(input.payload.pluginSlug) ?? undefined,
                pluginLabel: readString(input.payload.pluginLabel) ?? undefined,
                adapterSlug: readString(input.payload.adapterSlug) ?? undefined,
                runtimeSurface: readString(input.payload.runtimeSurface) ?? undefined,
                scope: readObject(input.payload.scope) ?? undefined,
                presentation: toolPresentationSchema.optional().parse(input.payload.presentation),
            };
            if (input.kind === "tool.started") {
                return taskRunEventSchema.parse({
                    ...base,
                    kind: input.kind,
                    payload: {
                        ...shared,
                        args: input.payload.args,
                    },
                });
            }
            if (input.kind === "tool.updated") {
                return taskRunEventSchema.parse({
                    ...base,
                    kind: input.kind,
                    payload: {
                        ...shared,
                        args: input.payload.args,
                        partialResult: input.payload.partialResult,
                    },
                });
            }
            return taskRunEventSchema.parse({
                ...base,
                kind: input.kind,
                payload: {
                    ...shared,
                    args: input.payload.args,
                    resultSize: readObject(input.payload.resultSize) ?? undefined,
                    result: input.payload.result,
                    queriedTables: input.payload.queriedTables,
                    queriedRelations: input.payload.queriedRelations,
                    certificateRef: twinExecutionCertificateRefSchema.optional().parse(input.payload.certificateRef),
                    executionReceipt: twinInvocationExecutionReceiptV1Schema.optional().parse(input.payload.executionReceipt),
                    isError: typeof input.payload.isError === "boolean"
                        ? input.payload.isError
                        : input.kind === "tool.failed",
                },
            });
        }
        default:
            return taskRunEventSchema.parse({
                ...base,
                kind: input.kind,
                payload: input.payload,
            });
    }
}
export const createTaskRunResponseSchema = z.object({
    runId: runIdSchema,
    sessionId: z.string().trim().min(1).optional(),
});
export const taskRunWithEventsSchema = z.object({
    run: taskRunSummarySchema.nullable(),
    events: z.array(taskRunEventSchema),
    attemptAttestations: z.array(candidateComponentBuildAttestationSchema).default([]),
});
export const TASK_RUN_HISTORY_PAGE_SIZE = 500;
export const taskRunHistoryCursorSchema = z.object({
    runId: runIdSchema, afterSeq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    throughSeq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
}).strict().refine(value => value.afterSeq <= value.throughSeq);
export const taskRunHistoryPageSchema = taskRunWithEventsSchema.extend({
    events: z.array(taskRunEventSchema).max(TASK_RUN_HISTORY_PAGE_SIZE),
    nextEventCursor: z.string().nullable(),
});
export const TASK_RUN_RECEIPT_BATCH_SIZE = 50;
export const TASK_RUN_RECEIPT_MAX_BYTES = 2 * 1024 * 1024;
export const taskRunAnswerReceiptsInputSchema = z.object({
    runIds: z.array(runIdSchema.max(200)).min(1).max(TASK_RUN_RECEIPT_BATCH_SIZE)
        .refine(ids => new Set(ids).size === ids.length, "Run IDs must be distinct"),
}).strict();
export const taskRunAnswerReceiptsResponseSchema = z.object({
    runs: z.array(z.object({ runId: runIdSchema, events: z.array(taskRunEventSchema) })
        .refine(run => run.events.every(event => event.runId === run.runId), "Receipt run mismatch"))
        .max(TASK_RUN_RECEIPT_BATCH_SIZE)
        .refine(runs => new Set(runs.map(run => run.runId)).size === runs.length, "Duplicate run receipts"),
});
export const taskRunsQueryResultSchema = z.union([
    taskRunWithEventsSchema,
    z.array(taskRunSummarySchema),
]);
// AGE-16: SDK-facing shape of a `taskRunMessages` row. The discriminated
// union over `parts[]` mirrors `assistantPartTypeSchema` above. Only
// text/reasoning/toolCall are produced by the worker today; the deferred
// kinds (artifactRef/citationSource/report/editorDoc) carry only
// `contentIndex` until a producer ships.
export const taskRunMessageTextPartSchema = z.object({
    type: z.literal("text"),
    text: z.string(),
    contentIndex: z.number().int().nonnegative(),
});
export const taskRunMessageReasoningPartSchema = z.object({
    type: z.literal("reasoning"),
    text: z.string(),
    contentIndex: z.number().int().nonnegative(),
});
export const taskRunMessageToolCallPartSchema = z.object({
    type: z.literal("toolCall"),
    toolCallId: z.string(),
    toolName: z.string(),
    args: z.unknown(),
    contentIndex: z.number().int().nonnegative(),
});
export const taskRunMessageArtifactRefPartSchema = z.object({
    type: z.literal("artifactRef"),
    contentIndex: z.number().int().nonnegative(),
});
export const taskRunMessageCitationSourcePartSchema = z.object({
    type: z.literal("citationSource"),
    contentIndex: z.number().int().nonnegative(),
});
export const taskRunMessageReportPartSchema = z.object({
    type: z.literal("report"),
    contentIndex: z.number().int().nonnegative(),
});
export const taskRunMessageEditorDocPartSchema = z.object({
    type: z.literal("editorDoc"),
    contentIndex: z.number().int().nonnegative(),
});
export const taskRunMessagePartSchema = z.discriminatedUnion("type", [
    taskRunMessageTextPartSchema,
    taskRunMessageReasoningPartSchema,
    taskRunMessageToolCallPartSchema,
    taskRunMessageArtifactRefPartSchema,
    taskRunMessageCitationSourcePartSchema,
    taskRunMessageReportPartSchema,
    taskRunMessageEditorDocPartSchema,
]);
export const taskRunMessageRoleSchema = z.enum(["assistant"]);
export const taskRunMessageStatusSchema = z.enum(["streaming", "complete"]);
export const taskRunMessageSchema = z.object({
    id: z.string(),
    runId: runIdSchema,
    role: taskRunMessageRoleSchema,
    parts: z.array(taskRunMessagePartSchema),
    status: taskRunMessageStatusSchema,
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const getTaskRunMessagesParamsSchema = z.object({
    runId: runIdSchema,
    paginationCursor: z.string().min(1).optional(),
});
export const getTaskRunMessagesResponseSchema = z.object({
    messages: z.array(taskRunMessageSchema),
    continueCursor: z.string().nullable(),
    isDone: z.boolean(),
});
// AGE-4: streamTaskRunEvents request shape. `runId` comes from the URL path;
// `afterEventId` is the opaque cursor emitted on prior SSE `id:` lines.
export const streamTaskRunEventsParamsSchema = z.object({
    runId: runIdSchema,
    afterEventId: z.string().min(1).optional(),
});
// AGE-4: terminal-frame payload. Marked with `x-mlx-sse-sentinel: true`
// in the OpenAPI spec so SDK clients can recognize terminal stream frames.
// AGE-17 widens the status to include `cancelled`.
export const taskRunEventSentinelSchema = z.object({
    runId: runIdSchema,
    status: z.enum(["completed", "failed", "cancelled"]),
});
// AGE-4: SSE frames use the standard `id`, `event`, `data`, `retry` top-level
// fields. These frame schemas wrap the semantic payload inside `data`.
//
// Wire format (server-written, unchanged):
//   id: <cursor>
//   event: <kind | "x-mlx-terminal-sentinel">
//   data: <JSON — TaskRunEvent for regular frames, {runId, status} for sentinel>
//
// The yielded element on the TypeScript SDK's AsyncIterable (under
// `sseFlatResponse: false`) is `{ id, event, data }` — callers still have
// access to the cursor via `.id` for reconnects.
export const streamTaskRunEventsEventFrameSchema = z.object({
    id: z.string().min(1),
    event: z.string().min(1),
    data: taskRunEventSchema,
});
export const streamTaskRunEventsSentinelFrameSchema = z.object({
    id: z.string().min(1),
    event: z.literal("x-mlx-terminal-sentinel"),
    data: taskRunEventSentinelSchema,
});
// AGE-17: cooperative cancel. Path-only request — no body. Response uses a
// stricter `reason` enum than the worker-facing schema: `lease_mismatch` is
// impossible from the consumer route (no lease check), and `cancelled` is
// also pruned (the cancel mutation is idempotent, so an already-cancelled
// run returns ok rather than a reason). `not_running` is reserved for
// future expansion if a hypothetical "only cancel queued|running" mode is
// added; the v1 mutation never returns it.
export const cancelTaskRunInputSchema = z.object({
    runId: runIdSchema,
});
export const cancelTaskRunResponseSchema = z.object({
    ok: z.boolean(),
    reason: z.enum(["not_found", "not_running"]).optional(),
});
